import React from 'react'
import { cn } from '../../lib/utils'
import { useTheme } from '../../contexts/ThemeContext'
import { useMobile } from '../../contexts/MobileContext'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  glass?: boolean
  variant?: 'default' | 'glass' | 'elevated' | 'flat'
}

export function Card({ className, children, glass = false, variant = 'default', ...props }: CardProps) {
  const { theme } = useTheme()
  const { isMobile } = useMobile()
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const getVariantClasses = () => {
    switch (variant) {
      case 'glass':
        return isDark && !isMobile ? 'glass-card' : 'bg-surface border-outline'
      case 'elevated':
        return 'bg-surface border-outline shadow-lg'
      case 'flat':
        return 'bg-surface-secondary border-outline-secondary'
      default:
        return 'bg-surface border-outline shadow-md'
    }
  }

  return (
    <div
      className={cn(
        'rounded-lg border text-content transition-all duration-200',
        getVariantClasses(),
        glass && isDark && !isMobile && 'glass-card', // Apply glass effect only in dark mode and desktop
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function CardHeader({ className, children, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 p-6', className)}
      {...props}
    >
      {children}
    </div>
  )
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode
}

export function CardTitle({ className, children, ...props }: CardTitleProps) {
  return (
    <h3
      className={cn('text-2xl font-semibold leading-none tracking-tight text-content', className)}
      {...props}
    >
      {children}
    </h3>
  )
}

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode
}

export function CardDescription({ className, children, ...props }: CardDescriptionProps) {
  return (
    <p
      className={cn('text-sm text-content-secondary', className)}
      {...props}
    >
      {children}
    </p>
  )
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function CardContent({ className, children, ...props }: CardContentProps) {
  return (
    <div className={cn('p-6 pt-0', className)} {...props}>
      {children}
    </div>
  )
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function CardFooter({ className, children, ...props }: CardFooterProps) {
  return (
    <div className={cn('flex items-center p-6 pt-0', className)} {...props}>
      {children}
    </div>
  )
}
